import { logRocketApiKey } from '@bcf-vanilla-ts-v1-configurators/tokens/log-rocket/janusz/log-rocket-api-key';
import { registerLogRocketApiKey } from '@bcf-vanilla-ts-v1-configurators/tokens/log-rocket/token';
import { domainNumberStrExtractor } from '@bcf-vanilla-ts-v1-platforms/platform-worker/common/domain-number-extractor';
import { registerDiToken } from '@bcf-vanilla-ts-v1-shared/di/di-token';
import type { DeviceInfoPayload } from '@bcf-vanilla-ts-v1-utilities/device-info/types';
import { extractDevice } from '@bcf-vanilla-ts-v1-utilities/device-info/utils';
import { registerPwaDeviceContext } from '@bcf-vanilla-ui-ts-v1/shared/pwa/pwa-device-context';
import { registerEnvBase } from '@bcf2-envs/env-token';
import { campaignIdsWithExpirationDate } from '@bcf2-v1-configurators-tokens/affiliate/janusz-casino/affiliate-ids-with-expiration-date';
import { CAMPAIGN_IDS_WITH_EXPIRATION_DATE } from '@bcf2-v1-configurators-tokens/affiliate/token';
import { APP_RELEASE_VERSION } from '@bcf2-v1-configurators-tokens/app/token';
import { I18N_OPTIMOVE_DESC_LOADER } from '@bcf2-v1-configurators-tokens/descs-optimove-i18n/token';
import { I18nPushSubscriptionBannerDesc } from '@bcf2-v1-configurators-tokens/descs-optimove-i18n/types';
import { imgWebConfig } from '@bcf2-v1-configurators-tokens/img-webp/janusz-casino/img-web-config';
import { IMG_WEB_CONFIG } from '@bcf2-v1-configurators-tokens/img-webp/token';
import { sportsbookMobileOptiLitswRegistrationConfig } from '@bcf2-v1-configurators-tokens/litsw-registration/litsw-registration-presets';
import { LITSW_REGISTRATION_CONFIG } from '@bcf2-v1-configurators-tokens/litsw-registration/token';
import { IS_MOBILE_APP_AVAILABLE } from '@bcf2-v1-configurators-tokens/mobile-app/token';
import { OPTIMOVE_SDK_CONFIG_LOADER } from '@bcf2-v1-configurators-tokens/optimove-sdk/tokens';
import { OptimoveSdkConfigs } from '@bcf2-v1-configurators-tokens/optimove-sdk/types';
import { piqTheme } from '@bcf2-v1-configurators-tokens/piq-styles/janusz/piq-theme';
import { piqStylesMobile } from '@bcf2-v1-configurators-tokens/piq-styles/janusz/piq.mobile.styles';
import { PIQ_CSS, PIQ_THEME } from '@bcf2-v1-configurators-tokens/piq-styles/token';
import { wizardPostFormHiddenFieldsWithoutPromocode } from '@bcf2-v1-configurators-tokens/wizard-post/curacao/wizard-form-post-hidden-fields';
import { cpWizardPostFormLayoutConfig } from '@bcf2-v1-configurators-tokens/wizard-post/curacao/wizard-post-form-layout-config';
import {
  CP_WIZARD_POST_FORM_LAYOUT_CONFIG,
  WIZARD_POST_FORM_HIDDEN_FIELDS
} from '@bcf2-v1-configurators-tokens/wizard-post/token';
import { configureSvgIcons } from '@bcfds-design-system/components/svg-icon/configurator';
import { envCommonToLit20EnvBase } from '@bcflit-env/env-converter';
import { EnvCommon } from '@bcflit-env/types';
import { releaseVersion } from '@bcflit-release-version';
import { APP_WITH_WORKER } from '@bcflit-v1-configurators/tokens/app/token';
import { USER_PANEL_DISPLAY_TIME_IN_LOCAL_TIME_ZONE } from '@bcflit-v1-configurators/tokens/date-format/token';
import { VERIFICATION_I18N_LOADER } from '@bcflit-v1-configurators/tokens/desc/verification/token';
import { I18nVerificationDesc } from '@bcflit-v1-configurators/tokens/desc/verification/types';

export function registerSettingsProviders(envCommon: EnvCommon): void {
  registerDiToken(APP_RELEASE_VERSION, () => releaseVersion);
  registerEnvBase(envCommonToLit20EnvBase(envCommon));
  const deviceInfo: DeviceInfoPayload = extractDevice('mobile-web', window, navigator);
  registerPwaDeviceContext({
    isMobile: () => deviceInfo.isMobile,
    isPWA: () => deviceInfo.isPwa,
    isIosDevice: () => deviceInfo.isIos,
    isSafari: () => deviceInfo.isSafari
  });
  registerDiToken(IMG_WEB_CONFIG, () => imgWebConfig);
  registerDiToken(CP_WIZARD_POST_FORM_LAYOUT_CONFIG, () => cpWizardPostFormLayoutConfig);
  registerDiToken(WIZARD_POST_FORM_HIDDEN_FIELDS, () => wizardPostFormHiddenFieldsWithoutPromocode);
  registerDiToken(APP_WITH_WORKER, () => true);
  registerDiToken(PIQ_THEME, () => piqTheme);
  registerDiToken(PIQ_CSS, () => piqStylesMobile);
  registerDiToken(CAMPAIGN_IDS_WITH_EXPIRATION_DATE, () => campaignIdsWithExpirationDate);
  registerDiToken(IS_MOBILE_APP_AVAILABLE, () => true);
  registerDiToken(
    VERIFICATION_I18N_LOADER,
    () => (): Promise<I18nVerificationDesc | undefined> =>
      // eslint-disable-next-line @typescript-eslint/typedef
      import('@bcflit-v1-configurators/tokens/desc/verification/janusz-casino/i18n-descs').then((mod) => mod.i18nDesc)
  );
  registerDiToken(
    OPTIMOVE_SDK_CONFIG_LOADER,
    () => (): Promise<OptimoveSdkConfigs> =>
      import('@bcf2-v1-configurators-tokens/optimove-sdk/janusz-casino/sportsbook/mobile-web/optimove-sdk-config').then(
        // eslint-disable-next-line @typescript-eslint/typedef
        (mod) => mod.optimoveSdkConfigs
      )
  );
  registerDiToken(
    I18N_OPTIMOVE_DESC_LOADER,
    () => (): Promise<I18nPushSubscriptionBannerDesc | undefined> =>
      import('@bcf2-v1-configurators-tokens/descs-optimove-i18n/janusz-casino/sportsbook/i18n-optimove-desc').then(
        // eslint-disable-next-line @typescript-eslint/typedef
        (mod) => mod.i18nOptimoveDesc
      )
  );
  configureSvgIcons({
    domainNumberStr: domainNumberStrExtractor(window.location.hostname),
    svgIconConfig: envCommon.svgIconConfig
  });
  registerDiToken(USER_PANEL_DISPLAY_TIME_IN_LOCAL_TIME_ZONE, () => true);
  registerLogRocketApiKey(logRocketApiKey);
  registerDiToken(LITSW_REGISTRATION_CONFIG, () => sportsbookMobileOptiLitswRegistrationConfig);
}
